import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalBody
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchArticleActionReport,
  fetchArticleActionReportTableColumnNames,
  fetchArticleTableColumnNames,
  fetchLawDocuments,
  updateUserPreference,
  updateJsonField,
  updateJsonStatus
} from "../../../slices/thunks";
import { setPage, setPerPage, updateArticleActionReport, clearArticleActionReport } from "../../../slices/articleActionReport/reducer";
import { getAuthUser } from "../../../helpers/utils";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import usePermissions from "../../../Components/Hooks/usePermissions";
import LawDocumentModal from "../../../Components/Common/Modal/LawDocumentModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { columns } from "./DataTableColumns";
import * as url from "../../../helpers/url_helper";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";

const customStyles = {
  headCells: {
    style: {
      backgroundColor: '#F2F4F7',   // header background
      color: '#333',               // header text color
      fontWeight: '600',
      fontSize: '14px',
      paddingTop: '12px',
      paddingBottom: '12px',
    },
  },
  headRow: {
    style: {
      backgroundColor: '#F2F4F7',   // full row background
      minHeight: '48px',
    },
  },
};

const statusObj = {
  "new": 1,
  "reviewed": 2,
  "all": "all"
}

const ITEMS_PER_PAGE = 10;


function ArticleActionReport() {
  const { user } = getAuthUser();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSaving, setIsSaving] = useState(false);
  const [showLawModal, setShowLawModal] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedRowKey, setSelectedRowKey] = useState(null)
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get("status") ? searchParams.get("status") : 'new');
  const [fileUrl, setFileUrl] = useState(null);
  // const [data, setData] = useState([]);
  

  const {articleActionReport, loading, error, tableColumnNames, articleColumnNames, totalRecords, perPage, page } = useSelector(
    (state) => ({
      tableColumnNames: state.ArticleActionReport.tableColumnNames,
      articleColumnNames: state.ArticleActionReport.articleColumnNames,
      loading: state.ArticleActionReport.loading,
      error: state.ArticleActionReport.error,
      articleActionReport: state.ArticleActionReport.documents,
      totalRecords: state.ArticleActionReport.totalRecords,
      perPage: state.ArticleActionReport.perPage,
      page: state.ArticleActionReport.page,
    })
  );

  const filteredData = useMemo(() => {
    if (!Array.isArray(articleActionReport)) return [];

    const lowerSearchTerm = searchTerm.toLowerCase();

    // return articleActionReport;

    return articleActionReport.filter((item) => {
      // ✅ STATUS FILTER FIRST (FAST EXIT)

      // ✅ If no search term, keep the row
      if (!lowerSearchTerm) return true;

      // ✅ SEARCH FILTER ON ROW DATA (NOT column config)
      return Object.values(item).some((value) => {
        if (!value) return false;

        // ✅ Handle nested objects
        if (typeof value === "object") {
          return Object.values(value).some((nestedVal) =>
            nestedVal?.toString().toLowerCase().includes(lowerSearchTerm)
          );
        }

        return value.toString().toLowerCase().includes(lowerSearchTerm);
      });
    });
  }, [articleActionReport, searchTerm]);

  const debouncedSearch = useCallback(
    debounce(async (value) => {
      await dispatch(clearArticleActionReport())
      dispatch(fetchArticleActionReport({ ArticleActionStatus: statusObj[activeTab], DocNumber: value, Page: page, Limit: perPage }));
    }, 2000), // <-- 500ms delay
    [activeTab, dispatch]
  );

  const handleDocSearch = (e) => {
    const value = e.target.value;
    setDocNumber(value);
    debouncedSearch(value);
  };

  const handlePageChange = async (newPage) => {
    dispatch(setPage(newPage));
    await dispatch(fetchArticleActionReport({ ArticleActionStatus: statusObj[activeTab], DocNumber: docNumber, Page: newPage, Limit: perPage })).unwrap();
  }

  const handlePerPageChange = async (newPerPage) => {
    dispatch(setPerPage(newPerPage));
    await dispatch(fetchArticleActionReport({ ArticleActionStatus: statusObj[activeTab], DocNumber: docNumber, Page: 1, Limit: newPerPage })).unwrap();
  }
    
  const handleTabChange = async (tab) => {
    await dispatch(clearArticleActionReport());
    dispatch(fetchArticleActionReport({ ArticleActionStatus: statusObj[tab], DocNumber: docNumber, Page: 1, Limit: perPage }));
    setSearchParams({ status: tab });
    setActiveTab(tab);
    setFileUrl(null);
  }

  const onSaveClickHandler = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const payload = {
        DocID: selectedRow.ID,
        JsonURL: selectedRow.JsonURL,
        DocumentTypeID: selectedRow.DocumentTypeID,
        Status: selectedRow.Status,
        pushToPinecone: false,
        documents: selectedRow.Json
      }

      const response = await fetch(url.SAVE_DOC_DETAILS, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload), 
      });

      if (!response.body) throw new Error("No response body for stream");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      // Process streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // keep last incomplete chunk

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          let eventName = "";
          let eventData = "";

          for (const line of eventBlock.split("\n")) {
            if (line.startsWith("event:"))
              eventName = line.slice(6).trim();
            else if (line.startsWith("data:"))
              eventData += line.slice(5).trim();
          }

          if (!eventName || !eventData) continue;

          try {
            const parsedData = JSON.parse(eventData);
            if (eventName === "error") {
              throw new Error('Something Went Wrong')
            }

            if (eventName === "file_saved") {
              dispatch(updateArticleActionReport(selectedRow));
              dispatch(updateJsonField({ ID: selectedRow.ID, Field: `${selectedRowKey}${selectedRow.article_number_numeric}`}))
              setSelectedRowKey();
              setSelectedRow({});
              setShowLawModal(false);
            }

          } catch (err) {
            toast.error(t('Something Went Wrong'));
            console.warn("Invalid SSE event data:", eventData, err);
          }
        }
      }
    } catch (error) {
      console.error("Save stream error:", error);
    } finally {
      setIsSaving(false); // ✅ only after stream fully ends
    }
  }

  const saveUserPreferences = async () => {
    const preferencesPayload = {
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ID] = selectedColumns.includes(column.ID);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchLegalRankTableColumnNames({
          module: "LAW_REPORT",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const handleViewClick = (item) => {
    setSelectedRow(item);
    setFileUrl(item.FileURL);
  }

  const handleEditJsonClick = (item) => {
    navigate('/ai/document-details', { 
      state:  {
        DocID: item.ID,
        FileName: item.FileName,
        FileURL: item.FileURL,
        JsonURL: item.JsonURL,
        IsSaved: item.IsSaved,
        Status: item.Status,
        ReviewedBy: item.ReviewedBy,
        ParaLegalIDs: item.ParaLegalIDs,
        DocumentTypeID: item.DocumentTypeID
      }
    })
  }

  const handleStatusChange = (item) => {
    setSelectedRow(item);
    setShowStatus(true);
  }

  const UpdateDocumentStatus = async () => {
    if (isSaving) return;
    
    setIsSaving(true);
    try {
      const payload = {
        DocID: selectedRow.ID,
        JsonURL: selectedRow.JsonURL,
        DocumentTypeID: selectedRow.DocumentTypeID,
        Status: selectedRow.Status,
        pushToPinecone: true,
        documents: selectedRow.Json
      }

      const response = await fetch(url.SAVE_DOC_DETAILS, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload), 
      });

      if (!response.body) throw new Error("No response body for stream");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      // Process streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // keep last incomplete chunk

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          let eventName = "";
          let eventData = "";

          for (const line of eventBlock.split("\n")) {
            if (line.startsWith("event:"))
              eventName = line.slice(6).trim();
            else if (line.startsWith("data:"))
              eventData += line.slice(5).trim();
          }

          if (!eventName || !eventData) continue;

          try {
            const parsedData = JSON.parse(eventData);
            if (eventName === "error") {
              throw new Error('Something Went Wrong')
            }

            if (eventName === "file_saved") {
              dispatch(updateJsonStatus({ ID: selectedRow.ID, ArticleActionStatus: 2 }))
              setSelectedRow({});
              setShowStatus(false);
            }

          } catch (err) {
            toast.error(t('Something Went Wrong'));
            console.warn("Invalid SSE event data:", eventData, err);
          }
        }
      }
    } catch (error) {
      console.error("Save stream error:", error);
    } finally {
      setIsSaving(false); // ✅ only after stream fully ends
    }
  };

  // Compute permission
  const permission = (() => {
    return usePermissions(1, 21);
  })();

  const actionConfig = {
    view: { 
      icon: "ri-eye-line",
      tooltip: t("View"),
      onClick: handleViewClick,
      isVisible: (item, perm) => true, // Show for all items, or add permission check: perm?.CanView
    },
    edit: {
      icon: "ri-edit-2-line",
      tooltip: t("Edit Json"),
      onClick: handleEditJsonClick,
      isVisible: (item, perm) => true
    },
    save: {
      icon: "ri-save-line",
      tooltip: t("Save"),
      onClick: handleStatusChange,
      isVisible: (item, perm) => {
        const fields = item.JsonFieldUpdated ?? [];
        // const articles = (item.Articles || []).map((article) => article.article_number_numeric?.toString())
        const articles = item.Articles.flatMap(article =>
          article.Changes.map(change => `${change}${article.article_number_numeric}`)
        );
        return articles.every(f => fields.includes(f)) || searchParams.get("status") === 'reviewed'; // Show only if all articles are updated or status is reviewed
      }
    },
  };


  const handleRowClick = (item, key) => {
    const updatedItem = JSON.parse(JSON.stringify(item));

    updatedItem.Path = ['article_actions', key]
    updatedItem.article_actions = {
      ...(updatedItem.article_actions || {}),
      [key]: updatedItem.article_actions[key].map((i) => {
        return {
          ...i,
          document_id: i.document_id || ''
        }
      })
    }

    setSelectedRowKey(key);
    setSelectedRow(updatedItem);
    setShowLawModal(true)
  }


  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch(fetchArticleActionReport({ ArticleActionStatus: statusObj[activeTab], DocNumber: docNumber, Page: 1, Limit: ITEMS_PER_PAGE }));
        dispatch(fetchArticleActionReportTableColumnNames({ module: "ARTICLE_ACTION_REPORT" }))
        dispatch(fetchArticleTableColumnNames({ module: "ARTICLE" }))
        dispatch(fetchLawDocuments());
        
      } catch (error) {
        console.error(error);
      }
    };

    dispatch(setPerPage(ITEMS_PER_PAGE));
    fetchData();
  }, [dispatch]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={t("Article Action Report")} />
          </Col>
        </Row>

        <div className="table-responsive">
          <Card>
            <CardHeader>
                <Row className="g-4 ">
                  <Col className="col-sm-auto">
                    <div>
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search By Document Number")}
                          value={docNumber}
                          onChange={handleDocSearch}
                        />
                        <i className="ri-file-search-line search-icon"></i>
                      </div>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
                <Nav tabs style={{ marginBottom: 16 }}>
                  <NavItem>
                    <NavLink
                      className={activeTab == 'new' ? "active" : ""}
                      onClick={() => handleTabChange('new')}
                      style={{ cursor: "pointer" }}
                    >
                      {t('New')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab == 'reviewed' ? "active" : ""}
                      onClick={() => handleTabChange('reviewed')}
                      style={{ cursor: "pointer" }}
                    >
                      {t('Reviewed')}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab == 'all' ? "active" : ""}
                      onClick={() => handleTabChange('all')}
                      style={{ cursor: "pointer" }}
                    >
                      {t('All')}
                    </NavLink>
                  </NavItem>
                </Nav>
             
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                    <Spinner
                      style={{ height: "3rem", width: "3rem" }}
                      className="me-2"
                    >
                      Loading...
                    </Spinner>
                  </div>
                ) : (
                  <Row className="justify-content-center" style={{ height: "80vh" }}>
                    <Col md={fileUrl ? 8 : 12}
                      style={{
                        height: "100%",
                        overflowY: "auto", // vertical scrollbar for PDF
                      }}
                    >
                      
                      <DataTable
                        data={filteredData}
                        columns={columns(tableColumnNames, i18n, t, permission, actionConfig, handleRowClick)}
                        expandableRows
                        expandableRowsComponent={({ data }) => (
                          <div style={{ padding: "20px 30px", margin: "10px 40px", border: "1px solid #ddd", borderRadius: "5px" }}>
                            <DataTable
                              columns={columns(articleColumnNames, i18n, t, permission, actionConfig, handleRowClick)}
                              data={data.Articles || []}
                              striped
                              responsive
                              highlightOnHover
                              pagination={false}
                              noDataComponent={
                                <div style={{ padding: '24px', textAlign: 'center' }}>
                                  {t('There are no records to display')}
                                </div>
                              }
                            />
                          </div>
                        )}

                        responsive
                        striped
                        highlightOnHover

                        pagination    
                        paginationServer            
                        paginationDefaultPage={page}                // ✅ MUST be boolean
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 50, 100, 200]}
                        paginationTotalRows={totalRecords}

                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerPageChange}
                        customStyles={customStyles}
                        noDataComponent={
                          <div style={{ padding: '24px', textAlign: 'center' }}>
                            {t('There are no records to display')}
                          </div>
                        }

                        paginationComponentOptions={{
                          rowsPerPageText: t('Rows per page'),
                          rangeSeparatorText: t('of'),
                          selectAllRowsItem: false,
                        }}
                      />

                    </Col>
                    {fileUrl && (
                      <Col
                        md={4}
                        style={{
                          position: "relative",
                          height: "100%",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        {/* Close Button */}
                        <div className="d-flex flex-column">
                          <div className="d-flex justify-content-between align-items-start mb-3">

                            {/* File Name (truncate long text) */}
                            <div
                              className="flex-grow-1 text-truncate"
                              style={{ maxWidth: "85%" }}
                            >
                              {selectedRow.FileName}
                            </div>

                            {/* Close Button */}
                            <Button
                              size="sm"
                              color="danger"
                              style={{ width: "32px", height: "32px", padding: 0, marginLeft: "10px" }}
                              onClick={() => {
                                setFileUrl(null);
                                setSelectedRow({});
                              }}
                            >
                              ×
                            </Button>
                          </div>

                          <iframe
                            src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${fileUrl}`}
                            width="100%"
                            height="95%"
                            style={{ minHeight: "600px" }}
                          />
                        </div>
                      </Col>
                    )}
                  </Row>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={saveUserPreferences}
      />

      <LawDocumentModal
        show={showLawModal}
        isSaving={isSaving}
        fieldKey={selectedRowKey}
        selectedRow={selectedRow}
        setShow={setShowLawModal}
        setSelectedRow={setSelectedRow}
        onSaveClickHandler={onSaveClickHandler}
        disabled={
          isSaving ||
          !selectedRowKey ||
          (Array.isArray(selectedRow?.article_actions?.[selectedRowKey]) &&
          selectedRow.article_actions[selectedRowKey].some(
            (item) => !item?.document_id && !item?.new_law_number
          ))
        }
      />

      <Modal isOpen={showStatus} centered={true}>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('areYouSure')}</h4>
              <p className="text-muted mx-4 mb-0">{t('Are you sure you want to update the status of this document?')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              data-bs-dismiss="modal"
              onClick={() => setShowStatus(false)}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger d-flex align-items-center"
              disabled={isSaving}
              onClick={UpdateDocumentStatus}
            >
              {t('Yes, Update it')}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default ArticleActionReport;